<template>
  <div dir="ltr">
    <Transition name="stage-fade" mode="out-in">
      <StageComponent
        @stageClicked="stageClicked"
        v-if="
          initialized &&
          !disableStage &&
          !tradeinId &&
          $t('i18n.stage.image' || false)
        "
      />
    </Transition>

    <template v-if="isMobile">
      <StickySummary
        v-if="step.data && step.data.summary"
        :summary="step.data.summary"
        :residualValue="step.data.residualValue"
        :isMobile="isMobile"
        :showResidualValue="
          step.stepId !== 'vehicledata' &&
          step.stepId !== 'emailrequest' &&
          step.stepId !== 'emailconfirmation'
        "
      />
    </template>

    <div id="tradein" class="ti" ref="ti">
      <Transition name="header-fade">
        <SkeletonLoaderComponent v-if="!step.data" />
        <div
          class="ti__wrapper"
          ref="tiWrapper"
          v-else-if="
            step.data &&
            step.stepId !== 'ApplicationError' &&
            step.stepId !== 'ratelimit' &&
            step.stepId !== 'applicationmaintenance'
          "
        >
          <HeaderComponent
            v-if="
              step.stepId !== 'dealerconfirmation' &&
              step.stepId !== 'emaildealerconfirmation'
            "
            :headline="'i18n.general.headline'"
            :copy="'i18n.general.copy'"
          />

          <HeaderComponent v-else :headline="step.data.headline" />

          <div class="ti__content">
            <StepperCe
              class="ti__stepper"
              :step="step"
              :loading="loading"
              :updating="updating"
              :history="history"
              :notification="notification"
              @closeNotification="closeNotification"
              @navigateToStep="navigateToStep"
              @stepChanged="stepChanged"
              @stepChangedAndNext="stepChangedAndNext"
              @nextClicked="nextClicked"
              @prevClicked="prevClicked"
              @getStep="getStep"
            />

            <Transition name="benefits-fade">
              <StickyBenefits
                class="ti__sticky-benefits"
                v-if="!step.data.summary"
              />
            </Transition>

            <Transition name="summary-fade">
              <StickySummary
                class="ti__sticky-summary"
                v-if="step.data.summary && !isMobile"
                :summary="step.data.summary"
                :residualValue="step.data.residualValue"
                :isMobile="isMobile"
                :showResidualValue="
                  step.stepId !== 'vehicledata' &&
                  step.stepId !== 'emailrequest' &&
                  step.stepId !== 'emailconfirmation'
                "
              />
            </Transition>
          </div>

          <template
            v-if="
              step.stepId === 'emaildealerconfirmation' ||
              step.stepId === 'dealerconfirmation'
            "
          >
            <ConfirmationSection
              v-if="step.data.nbas"
              :nbas="step.data.nbas.actionItems"
            />
          </template>
        </div>

        <div
          class="ti__wrapper"
          v-else-if="
            step.data &&
            (step.stepId === 'ApplicationError' ||
              step.stepId === 'applicationmaintenance' ||
              step.stepId === 'ratelimit')
          "
        >
          <ApplicationErrorComponent
            :headline="getErrorText(step.stepId, 'headline')"
            :copy="getErrorText(step.stepId)"
            :dealerCopy="getErrorText(step.stepId, 'dealerCopy')"
          />
        </div>
      </Transition>

      <ApplicationInfosComponent
        v-if="applicationInfos && applicationInfos.environment !== 'prod'"
        @applySettings="applySettings"
        @disableStage="disableStage = $event"
      />
    </div>

    <UnauthorizedModalComponent
      v-if="showUnauthorizedModal"
    ></UnauthorizedModalComponent>
    <div id="modal-target"></div>
  </div>
</template>
<script setup lang="ts">
import { PersistentConnection } from '@seamless/store';
import { Step } from '@tradein/api';
import { Notification, StepIds, tiConnectionName } from '@tradein/store';
import { scrollToElement } from '@tradein/utils';
import { onMounted, Ref, ref, watch } from 'vue';
import setup from './composables/setup';
import { trackingService } from './utils/tracking';
import { changesValid } from './utils/utils';
import i18nManager from './composables/i18nManager';

import HeaderComponent from './components/header/header.vue';
import StageComponent from './components/stage/stage.vue';
import ApplicationInfosComponent from './components/application-infos/application-infos.vue';
import UnauthorizedModalComponent from './components/unauthorized-modal/unauthorized-modal.vue';
import StepperCe from './templates/stepper/stepper.vue';
import StickyBenefits from './components/sticky-benefits/sticky-benefits.vue';
import StickySummary from './components/sticky-summary/sticky-summary.vue';
import ConfirmationSection from './templates/confirmation-section/confirmation-section.vue';
import ApplicationErrorComponent from './templates/application-error/application-error.vue';
import SkeletonLoaderComponent from './components/skeleton-loader/skeleton-loader.vue';

const {
  setupStoreConnection,
  setupConfigurationIdAcceptLanguage,
  getConnectionDispatchers,
  setupWKOListener,
  setupMediaQueryListener,
  startWorkflow,
  setupApplicationInfos,
  setApplicationInfos,
  setupAuthenticationListener,
  setupComponentRegistryListener,
  configurationId,
  tradeInStoreDispatchers,
  tradeInStoreConnection,
  currentMediaQuery,
  store,
  initialized,
  utmParams,
  tradeinId,
  applicationInfos,
} = setup();

const step: Ref<Step> = ref({} as Step);
const loading = ref(false);
const updating = ref(false);
const showUnauthorizedModal = ref(false);
const history: Ref<Step[]> = ref([]);
const notification: Ref<Notification> = ref({});
const disableStage = ref(false);
const isMobile = ref(false);
const mobileMediaQuery = window.matchMedia('(max-width: 1023px)');
const { i18n } = i18nManager();

const tiWrapper: Ref<HTMLElement> = ref({} as HTMLElement);
const ti: Ref<HTMLElement> = ref({} as HTMLElement);

watch(initialized, (value) => {
  startWorkflow();
});

watch(
  () => step.value.stepId,
  (newStepId) => {
    if (
      newStepId === 'emaildealerconfirmation' ||
      newStepId === 'dealerconfirmation'
    ) {
      scrollToElement(ti.value, 0, {
        currentMediaQuery: currentMediaQuery.value
          ? currentMediaQuery.value
          : 'mq1',
        enableForMediaQuery: ['mq1', 'mq2', 'mq3', 'mq4', 'mq5', 'mq6'],
      });
    }
  },
);

onMounted(() => {
  setupConfigurationIdAcceptLanguage();
  setupApplicationInfos();
  setupMediaQueryListener();
  setupStoreConnection().then(() => {
    getConnectionDispatchers().then(() => {
      setupWKOListener();
      setupAuthenticationListener();
      setupComponentRegistryListener();
    });
  });

  store.value.subscribe(tiConnectionName, (state) => {
    showUnauthorizedModal.value = state?.showUnauthorizedModal;

    if (state && state.step) {
      step.value = state.step;
      history.value = state.history;
      loading.value = state.loading;
      updating.value = state.updating;
      notification.value = state.notification;

      interfaceBlocker(loading.value || updating.value);

      trackingService.setStepInformation(step.value);

      if (state.applicationInfos) {
        setApplicationInfos(state.applicationInfos);
      }
    }
  });

  mobileMediaQuery.addListener(handleMediaQueryChange);
  isMobile.value = mobileMediaQuery.matches;

  trackingService.getFirstInteraction(ti.value);
});

const handleMediaQueryChange = (change: MediaQueryListEvent) => {
  isMobile.value = change.matches;
};

const stageClicked = (): void => {
  scrollToElement(ti.value, 0, {
    currentMediaQuery: currentMediaQuery.value
      ? currentMediaQuery.value
      : 'mq1',
    enableForMediaQuery: ['mq1', 'mq2', 'mq3', 'mq4', 'mq5', 'mq6'],
  });
};

const prevClicked = (): void => {
  tradeInStoreDispatchers.value.goToPrevStep();
};

const nextClicked = (data: { delay?: number }): void => {
  tradeInStoreConnection.value.postStep(
    configurationId.value,
    step.value.stepId,
    step.value,
    data && data.delay ? data.delay : 0,
    tradeInStoreDispatchers.value,
    utmParams.value,
  );
};

const navigateToStep = (stepId: string): void => {
  tradeInStoreDispatchers.value.goToStep(stepId);
};

const getStep = (stepId: string): void => {
  tradeInStoreConnection.value.getStep(
    configurationId.value,
    stepId,
    tradeInStoreDispatchers.value,
  );
};

const stepChanged = (step: Step): void => {
  if (
    step.form &&
    step.form.changes &&
    step.form.changes.length > 0 &&
    changesValid(step.form)
  ) {
    tradeInStoreConnection.value.updateStep(
      configurationId.value,
      step.stepId,
      step,
      tradeInStoreDispatchers.value,
    );
  } else {
    tradeInStoreDispatchers.value.setStep(step);
  }
};

const stepChangedAndNext = (data: Step): void => {
  tradeInStoreDispatchers.value.setStep(data);

  tradeInStoreConnection.value.postStep(
    configurationId.value,
    step.value.stepId,
    step.value,
    0,
    tradeInStoreDispatchers.value,
    utmParams.value,
  );
};

const closeNotification = (): void => {
  tradeInStoreDispatchers.value.closeNotification();
};

const applySettings = (): void => {
  PersistentConnection.removePersistedState(tiConnectionName, 'local');
};

const interfaceBlocker = (block: boolean) => {
  if (ti.value) {
    if (block) {
      ti.value.classList.add('-disabled');
    } else {
      ti.value.classList.remove('-disabled');
    }
  }
};

const getErrorText = (stepId: string, textType?: string): string => {
  if (textType === 'dealerCopy') {
    return i18n.value.t('i18n.applicationError.error.dealerCopy');
  } else {
    if (stepId === 'applicationmaintenance') {
      if (textType === 'headline') {
        return i18n.value.t('i18n.applicationError.maintenance.headline');
      } else {
        return i18n.value.t('i18n.applicationError.maintenance.copy');
      }
    } else if (stepId === 'ApplicationError') {
      if (textType === 'headline') {
        return i18n.value.t('i18n.applicationError.error.headline');
      } else {
        return i18n.value.t('i18n.applicationError.error.copy');
      }
    } else if (stepId === 'ratelimit') {
      if (textType === 'headline') {
        return i18n.value.t('i18n.rateLimit.feedback.headline');
      } else {
        return i18n.value.t('i18n.rateLimit.feedback.copy');
      }
    } else {
      if (textType === 'headline') {
        return i18n.value.t('i18n.applicationError.error.headline');
      } else {
        return i18n.value.t('i18n.applicationError.error.copy');
      }
    }
  }
};
</script>

<style lang="scss">
.benefits-fade-enter-active,
.benefits-fade-leave-active {
  transition: opacity 0.3s $wb-fade;
}

.benefits-fade-enter-from,
.benefits-fade-leave-to {
  opacity: 0;
}

.benefits-fade-enter-to,
.benefits-fade-leave-from {
  opacity: 1;
}

.summary-fade-enter-active,
.summary-fade-leave-active {
  opacity: 0;
  transition:
    opacity 0.1s $wb-fade 0.3s,
    transform 0.5s $wb-fade 0.3s;
}

.summary-fade-enter-from,
.summary-fade-leave-to {
  opacity: 0;
  transform: translateY(24px);
}

.summary-fade-enter-to,
.summary-fade-leave-from {
  opacity: 1;
  transform: translateY(0px);
}
</style>

<style src="./App.scss" lang="scss"></style>
